import { createSelector } from '@reduxjs/toolkit';
import { cond, constant, defaultTo, get, identity, pipe, split } from 'lodash/fp';
import { RootState } from '../types';
import { Buffer } from 'buffer';
import { AUTH_URL, LoginType } from 'src/utilities';

// There are 2 selectors for apiClientId:
// - getPreAPIClientId: gets the client ID which user puts in at login time
// - getAPIClientId: gets the client ID parsed from the access token after authentication
// This apiClientId can be different after login, because user can select a different client

const _getAppState = (state: RootState) => state.app;

export const getAppState = createSelector([_getAppState], (x) => x);
export const getPreAPIClientId = createSelector([getAppState], (x) => x.clientId);
export const getLoginType = createSelector([getAppState], (x) => x.loginType);
export const getRefreshToken = createSelector([getAppState], (x) => x.refreshToken);

export const getAuthorizationToken = createSelector([getAppState], (state) => {
  switch (state?.loginType) {
    case LoginType.SUPERUSER_GOOGLE:
      return state?.idToken;
    case LoginType.SSO:
    case LoginType.USER:
    default:
      return state?.accessToken;
  }
});

export const getIsLoggedIn = createSelector(
  [getAuthorizationToken, getPreAPIClientId],
  (token, apiClientID) => {
    return !!apiClientID && !!token;
  }
);

interface ParsedAccessToken {
  clientId: number;
  apiClientId: string;
  dbVersion: number;
  username: string;
  userId: number;
  isAdmin: boolean;
  ydUserId: string;
  picture?: string;
}

const _parseAccessToken = createSelector<[typeof getAuthorizationToken], ParsedAccessToken>(
  [getAuthorizationToken],
  pipe(
    defaultTo(''),
    split('.'),
    get('[1]'),
    cond([
      [identity, (x) => Buffer.from(x, 'base64').toString()],
      [constant(true), constant('')],
    ]),
    (x: string) => {
      try {
        return JSON.parse(x);
      } catch (err) {
        return {};
      }
    },
    ({ dbVersion, sub, name, userId, clientId, apiClientId, isAdmin, ...rest }) =>
      ({
        ...rest,
        clientId: +clientId,
        apiClientId,
        username: name ?? sub,
        dbVersion: dbVersion ? +dbVersion : null,
        userId: +userId,
        isAdmin: isAdmin?.toLowerCase?.() === 'true',
      } as ParsedAccessToken)
  )
);

export const getUsername = createSelector([_parseAccessToken], (x) => x?.username);
export const getProfilePhotoURL = createSelector([_parseAccessToken], (x) => x?.picture);
export const getDBVersion = createSelector([_parseAccessToken], (x) => x.dbVersion);

export const getClientId = createSelector([_getAppState, _parseAccessToken], (state, jason) => {
  switch (state?.loginType) {
    case LoginType.SUPERUSER_GOOGLE:
      return state?.clients?.find(
        (x) => x.apiClientID?.toLowerCase?.() === state.clientId?.toLowerCase?.()
      )?.clientId;
    case LoginType.SSO:
    case LoginType.USER:
    default:
      return jason?.clientId;
  }
});

export const getAPIClientId = createSelector([_getAppState, _parseAccessToken], (state, jason) => {
  switch (state?.loginType) {
    case LoginType.SUPERUSER_GOOGLE:
      return state?.clients?.find(
        (x) => x.apiClientID?.toLowerCase?.() === state.clientId?.toLowerCase?.()
      )?.apiClientID;
    case LoginType.SSO:
    case LoginType.USER:
    default:
      return jason?.apiClientId;
  }
});

export const getRefreshTokenURL = createSelector([getLoginType], (loginType) => {
  switch (loginType) {
    case LoginType.SUPERUSER_GOOGLE:
      return `${AUTH_URL}/refreshToken/google-superuser`;
    case LoginType.USER:
    default:
      return `${AUTH_URL}/refreshToken`;
  }
});
